import { useState, useEffect } from "react";
import api from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../hooks/use-toast";
import OdysboardView from "../shared/OdysboardView";
import ParcoursView from "../../components/ParcoursView";
import KPITooltip, { KPI_TOOLTIPS } from "../../components/KPITooltip";
import CoachInsightsPanel from "../../components/CoachInsightsPanel";
import NotificationPreferences from "../../components/NotificationPreferences";
import CoachRadar from "../../components/CoachRadar";
import AgendaView from "../../components/AgendaView";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, Area, AreaChart } from "recharts";

const StatusBadge = ({ status }) => {
  const map = { pending: "badge-pending", in_progress: "badge-in_progress", completed: "badge-completed" };
  const labels = { pending: "En attente", in_progress: "En cours", completed: "Terminé" };
  return <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${map[status] || "bg-slate-700 text-slate-300"}`}>{labels[status] || status}</span>;
};

const COLORS = ['#ef4444', '#f59e0b', '#10b981', '#8b5cf6'];

export default function CoachDashboard() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetail, setStudentDetail] = useState(null);
  const [steps, setSteps] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [showStepModal, setShowStepModal] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [stepForm, setStepForm] = useState({ title: "", description: "", deadline: "", status: "pending" });
  const [summary, setSummary] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [calendlyUrl, setCalendlyUrl] = useState(user?.calendly_url || "");
  const [savingCalendly, setSavingCalendly] = useState(false);
  const [fathomEmail, setFathomEmail] = useState(user?.fathom_email || "");
  const [savingFathom, setSavingFathom] = useState(false);
  const [fathomApiKey, setFathomApiKey] = useState("");
  const [savingFathomKey, setSavingFathomKey] = useState(false);
  const hasFathomApiKey = user?.has_fathom_api_key;
  const [myStats, setMyStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [nextAppts, setNextAppts] = useState({});
  const { toast } = useToast();

  useEffect(() => {
    fetchStudents();
    api.get("/calendar/next-appointments")
      .then(r => setNextAppts(r.data.appointments || {}))
      .catch(() => setNextAppts({}));
    setCalendlyUrl(user?.calendly_url || "");
  }, []);

  const fetchStudents = async () => {
    try {
      const res = await api.get("/coach/students");
      setStudents(res.data);
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de charger les élèves" });
    }
  };

  const fetchMyStats = async () => {
    setStatsLoading(true);
    try {
      const res = await api.get("/coach/my-stats");
      setMyStats(res.data);
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de charger les statistiques" });
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "my-stats" && !myStats) {
      fetchMyStats();
    }
  }, [activeTab]);

  const fetchStudentDetail = async (id) => {
    try {
      const [detailRes, stepsRes] = await Promise.all([
        api.get(`/coach/students/${id}`),
        api.get(`/steps?student_id=${id}`)
      ]);
      setStudentDetail(detailRes.data);
      setSteps(stepsRes.data);
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur" });
    }
  };

  const handleSelectStudent = (s) => {
    setSelectedStudent(s);
    fetchStudentDetail(s.id);
    setActiveTab("parcours");
  };

  const handleSaveStep = async () => {
    if (!stepForm.title.trim()) return;
    try {
      if (editingStep) {
        await api.put(`/steps/${editingStep.id}`, stepForm);
        toast({ title: "Étape modifiée" });
      } else {
        await api.post("/steps", { ...stepForm, student_id: selectedStudent.id });
        toast({ title: "Étape créée" });
      }
      setShowStepModal(false);
      setEditingStep(null);
      setStepForm({ title: "", description: "", deadline: "", status: "pending" });
      fetchStudentDetail(selectedStudent.id);
      fetchStudents();
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: e.response?.data?.detail || "Erreur" });
    }
  };

  const handleDeleteStep = async (id) => {
    if (!window.confirm("Supprimer cette étape ?")) return;
    try {
      await api.delete(`/steps/${id}`);
      toast({ title: "Étape supprimée" });
      fetchStudentDetail(selectedStudent.id);
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur" });
    }
  };

  const handleGenerateSummary = async () => {
    if (!selectedStudent) return;
    setSummaryLoading(true);
    try {
      const res = await api.post("/ai/summary", { student_id: selectedStudent.id });
      setSummary(res.data.summary);
      toast({ title: "Résumé généré" });
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur IA", description: e.response?.data?.detail || "Erreur" });
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleSaveCalendly = async () => {
    setSavingCalendly(true);
    try {
      await api.put("/coach/settings", { calendly_url: calendlyUrl });
      toast({ title: "URL Calendly mise à jour" });
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur" });
    } finally {
      setSavingCalendly(false);
    }
  };

  const handleSaveFathom = async () => {
    setSavingFathom(true);
    try {
      await api.put("/coach/settings", { fathom_email: fathomEmail });
      toast({ title: "Email Fathom mis à jour" });
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur" });
    } finally {
      setSavingFathom(false);
    }
  };

  const handleSaveFathomKey = async () => {
    setSavingFathomKey(true);
    try {
      await api.put("/coach/settings", { fathom_api_key: fathomApiKey });
      toast({ title: fathomApiKey ? "Clé API Fathom enregistrée" : "Clé API Fathom supprimée" });
      setFathomApiKey("");
      // Reload user to refresh has_fathom_api_key flag
      const me = await api.get("/auth/me");
      if (me.data) {
        localStorage.setItem("user", JSON.stringify(me.data));
        window.location.reload();
      }
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: e.response?.data?.detail || "Erreur" });
    } finally {
      setSavingFathomKey(false);
    }
  };

  const handleExportPDF = async (studentId) => {
    try {
      const response = await api.get(`/export/pdf/${studentId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `progression_eleve.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast({ title: "PDF téléchargé" });
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur export PDF" });
    }
  };

  // Prepare chart data
  const riskDistributionData = myStats ? [
    { name: "Risque élevé", value: myStats.high_risk, color: "#ef4444" },
    { name: "Risque modéré", value: myStats.medium_risk, color: "#f59e0b" },
    { name: "En bonne voie", value: myStats.low_risk, color: "#10b981" },
  ].filter(d => d.value > 0) : [];

  const tabs = [
    { key: "overview", label: "Mes élèves", icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" },
    { key: "my-stats", label: "Mes Stats", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
    ...(selectedStudent ? [
      { key: "parcours", label: "Parcours", icon: "M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" },
      { key: "visits", label: "Visites", icon: "M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" },
      { key: "notes", label: "Notes & Cahier", icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
      { key: "odysboard", label: "Odysboard", icon: "M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" },
      { key: "agenda", label: "Agenda", icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" },
    ] : []),
    { key: "settings", label: "Paramètres", icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="glass-card rounded-xl sm:rounded-2xl p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>Espace Coach</h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">Gérez vos élèves et leur progression</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="metric-card metric-card-cyan px-3 sm:px-4 py-2 sm:py-3">
              <p className="text-xl sm:text-2xl font-bold text-cyan-600">{students.length}</p>
              <p className="text-xs text-gray-500">Élèves</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs - Mobile: icons only, Desktop: icons + text */}
      <div className="flex gap-2 p-1.5 bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto scrollbar-hide mb-2">
        {tabs.map(t => (
          <button key={t.key} data-testid={`coach-tab-${t.key}`} onClick={() => setActiveTab(t.key)}
            className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex-1 sm:flex-initial min-w-[44px] ${
              activeTab === t.key 
                ? "bg-[#1a2744] text-white shadow-sm" 
                : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            }`}>
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={t.icon} />
            </svg>
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="space-y-4 animate-fade-in">
          {students.length > 0 && (
            <CoachRadar onSelectStudent={(sid) => {
              const s = students.find(st => st.id === sid);
              if (s) handleSelectStudent(s);
            }} />
          )}
          {students.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197" />
                </svg>
              </div>
              <p className="text-gray-500">Aucun élève affecté pour le moment</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {students.map(s => (
                <div key={s.id} data-testid={`student-card-${s.id}`}
                  onClick={() => handleSelectStudent(s)}
                  className="glass-card glass-card-hover rounded-2xl p-5 cursor-pointer">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1a2744] to-[#2d3e5f] flex items-center justify-center">
                      <span className="text-white font-bold">{s.first_name?.[0]}{s.last_name?.[0]}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{s.first_name} {s.last_name}</p>
                      <p className="text-xs text-gray-500">{s.email}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Progression</span>
                        <span className="font-semibold text-[#1a2744]">{s.progress}%</span>
                      </div>
                      <div className="progress-track">
                        <div className="progress-bar" style={{ width: `${s.progress}%` }} />
                      </div>
                    </div>
                    <div className="flex gap-4 text-xs text-gray-500">
                      <span>{s.completed_steps}/{s.total_steps} étapes</span>
                      <span>{s.visits_count} visites</span>
                    </div>
                    <div className="pt-2 border-t border-gray-100 text-xs" data-testid={`student-next-appt-${s.id}`}>
                      {nextAppts[s.id] ? (
                        <p className="text-[#C9A86A]">
                          <span className="text-gray-400">Prochain RDV : </span>
                          <span className="font-medium">{nextAppts[s.id].start_local}</span>
                        </p>
                      ) : (
                        <p className="text-gray-400">Aucun RDV à venir</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* My Stats Tab - NEW */}
      {activeTab === "my-stats" && (
        <div className="space-y-8 animate-fade-in">
          {statsLoading ? (
            <div className="space-y-4">
              {[1,2,3].map(i => <div key={i} className="skeleton h-24 rounded-2xl" />)}
            </div>
          ) : myStats ? (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                <div className="glass-card glass-card-hover rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="icon-circle icon-circle--ink">
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197" />
                      </svg>
                    </div>
                  </div>
                  <p className="metric-number">{myStats.students_count}</p>
                  <p className="metric-label mt-1">Élèves assignés</p>
                </div>
                
                <div className="glass-card glass-card-hover rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="icon-circle icon-circle--teal">
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <p className="metric-number">{myStats.success_rate}%</p>
                  <p className="metric-label mt-1">
                    <KPITooltip content={KPI_TOOLTIPS.successRate} position="bottom">
                      Taux de réussite
                    </KPITooltip>
                  </p>
                </div>
                
                <div className="glass-card glass-card-hover rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`icon-circle ${
                      myStats.avg_risk_score >= 60 ? "icon-circle--rouge" :
                      myStats.avg_risk_score >= 30 ? "" :
                      "icon-circle--teal"
                    }`}>
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                  </div>
                  <p className="metric-number">{myStats.avg_risk_score}%</p>
                  <p className="metric-label mt-1">
                    <KPITooltip content={KPI_TOOLTIPS.avgRiskScore} position="bottom">
                      Risque moyen
                    </KPITooltip>
                  </p>
                </div>
                
                <div className="glass-card glass-card-hover rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="icon-circle">
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                  </div>
                  <p className="metric-number">{myStats.activity?.month || 0}</p>
                  <p className="metric-label mt-1">
                    <KPITooltip content={KPI_TOOLTIPS.activityMonth} position="bottom">
                      Activité (30j)
                    </KPITooltip>
                  </p>
                </div>
              </div>

              {/* Risk Distribution Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="glass-card rounded-2xl p-5" style={{ borderColor: "rgba(154, 59, 46, 0.25)" }}>
                  <div className="flex items-center gap-3">
                    <div className="icon-circle icon-circle--rouge">
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div>
                      <p className="metric-number" style={{ color: "var(--rouge)" }}>{myStats.high_risk}</p>
                      <p className="metric-label" style={{ color: "var(--rouge-dark)" }}>
                        <KPITooltip content={KPI_TOOLTIPS.highRisk} position="bottom">
                          Risque élevé
                        </KPITooltip>
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="glass-card rounded-2xl p-5" style={{ borderColor: "var(--gold-soft)" }}>
                  <div className="flex items-center gap-3">
                    <div className="icon-circle">
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="metric-number" style={{ color: "var(--gold-dark)" }}>{myStats.medium_risk}</p>
                      <p className="metric-label" style={{ color: "var(--gold-dark)" }}>
                        <KPITooltip content={KPI_TOOLTIPS.mediumRisk} position="bottom">
                          Risque modéré
                        </KPITooltip>
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="glass-card rounded-2xl p-5" style={{ borderColor: "rgba(31, 111, 107, 0.25)" }}>
                  <div className="flex items-center gap-3">
                    <div className="icon-circle icon-circle--teal">
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="metric-number" style={{ color: "var(--teal)" }}>{myStats.low_risk}</p>
                      <p className="metric-label" style={{ color: "var(--teal-dark)" }}>
                        <KPITooltip content={KPI_TOOLTIPS.lowRisk} position="bottom">
                          En bonne voie
                        </KPITooltip>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Activity Trend Chart */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                  <h3 className="font-semibold text-slate-800 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Activité sur 6 mois
                  </h3>
                  {myStats.activity_trend?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <AreaChart data={myStats.activity_trend}>
                        <defs>
                          <linearGradient id="colorActivity" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip contentStyle={{ borderRadius: "12px", border: "1px solid #e2e8f0" }} />
                        <Area type="monotone" dataKey="count" stroke="#8b5cf6" fill="url(#colorActivity)" name="Sous-étapes créées" />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-slate-400">
                      Aucune donnée disponible
                    </div>
                  )}
                </div>

                {/* Risk Distribution Pie */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                  <h3 className="font-semibold text-slate-800 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Répartition des risques
                  </h3>
                  {riskDistributionData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={riskDistributionData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                        >
                          {riskDistributionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-slate-400">
                      Aucun élève assigné
                    </div>
                  )}
                </div>
              </div>

              {/* Students Progression Chart */}
              {myStats.students_progression?.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                  <h3 className="font-semibold text-slate-800 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Progression de mes élèves
                  </h3>
                  <ResponsiveContainer width="100%" height={Math.max(200, myStats.students_progression.length * 50)}>
                    <BarChart data={myStats.students_progression} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={120} />
                      <Tooltip 
                        contentStyle={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}
                        formatter={(value) => [`${value}%`, "Progression"]}
                      />
                      <Bar dataKey="progress" fill="#10b981" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Students at Risk Table */}
              {myStats.students_risk?.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
                  <div className="p-5 border-b border-slate-50 flex items-center justify-between">
                    <h3 className="font-semibold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>
                      Mes élèves - Détail des risques
                    </h3>
                    <span className="text-sm text-slate-500">{myStats.students_risk.length} élève{myStats.students_risk.length > 1 ? "s" : ""}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Élève</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Étape actuelle</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Score de risque</th>
                          <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Facteurs</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {myStats.students_risk.map(student => (
                          <tr key={student.student_id} className="hover:bg-slate-50 transition-colors">
                            <td className="px-5 py-3.5">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                  student.level === "high" ? "bg-red-100" :
                                  student.level === "medium" ? "bg-amber-100" :
                                  "bg-emerald-100"
                                }`}>
                                  <span className={`text-xs font-bold ${
                                    student.level === "high" ? "text-red-600" :
                                    student.level === "medium" ? "text-amber-600" :
                                    "text-emerald-600"
                                  }`}>
                                    {student.student_name?.split(" ").map(n => n[0]).join("")}
                                  </span>
                                </div>
                                <div>
                                  <p className="font-medium text-slate-800">{student.student_name}</p>
                                  <p className="text-xs text-slate-500">{student.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-3.5 text-slate-600">
                              {student.current_stage === "completed" ? (
                                <span className="inline-flex items-center gap-1 text-emerald-600">
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Terminé
                                </span>
                              ) : student.current_stage}
                            </td>
                            <td className="px-5 py-3.5">
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full ${
                                      student.level === "high" ? "bg-red-500" :
                                      student.level === "medium" ? "bg-amber-500" :
                                      "bg-emerald-500"
                                    }`}
                                    style={{ width: `${student.score}%` }}
                                  />
                                </div>
                                <span className={`text-sm font-semibold ${
                                  student.level === "high" ? "text-red-600" :
                                  student.level === "medium" ? "text-amber-600" :
                                  "text-emerald-600"
                                }`}>
                                  {student.score}%
                                </span>
                              </div>
                            </td>
                            <td className="px-5 py-3.5">
                              <div className="flex flex-wrap gap-1">
                                {student.factors?.slice(0, 2).map((f, i) => (
                                  <span key={i} className={`text-xs px-2 py-0.5 rounded ${
                                    f.impact === "high" ? "bg-red-100 text-red-700" :
                                    f.impact === "medium" ? "bg-amber-100 text-amber-700" :
                                    "bg-slate-100 text-slate-600"
                                  }`}>
                                    {f.name}
                                  </span>
                                ))}
                                {(!student.factors || student.factors.length === 0) && (
                                  <span className="text-xs text-slate-400">Aucun</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Performance Summary */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <h3 className="font-semibold text-slate-800 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Résumé de performance
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <p className="text-2xl font-bold text-slate-800">{myStats.completed_stages}</p>
                    <p className="text-xs text-slate-500 mt-1">Étapes validées</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <p className="text-2xl font-bold text-slate-800">{myStats.activity?.stages_validated_month || 0}</p>
                    <p className="text-xs text-slate-500 mt-1">Validées ce mois</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <p className="text-2xl font-bold text-slate-800">{myStats.abandonment_rate}%</p>
                    <p className="text-xs text-slate-500 mt-1">Taux d'abandon</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center justify-center gap-1">
                      {myStats.calendly_configured ? (
                        <svg className="w-6 h-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Calendly {myStats.calendly_configured ? "OK" : "Non configuré"}</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="glass-card rounded-2xl p-12 text-center">
              <p className="text-gray-500">Impossible de charger les statistiques</p>
              <button onClick={fetchMyStats} className="btn-primary mt-4">Réessayer</button>
            </div>
          )}
        </div>
      )}

      {/* Parcours Tab - New hierarchical system */}
      {activeTab === "parcours" && selectedStudent && (
        <div className="animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button onClick={() => { setSelectedStudent(null); setActiveTab("overview"); }} className="btn-ghost p-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
              </button>
              <div>
                <h3 className="font-semibold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>{selectedStudent.first_name} {selectedStudent.last_name}</h3>
                <p className="text-xs text-gray-500">Parcours investisseur</p>
              </div>
            </div>
            <button 
              onClick={() => {
                const token = localStorage.getItem("token");
                window.open(`${process.env.REACT_APP_BACKEND_URL}/api/export/pdf/${selectedStudent.id}?token=${token}`, "_blank");
              }}
              className="btn-secondary flex items-center gap-2 text-sm"
              data-testid="export-pdf-btn"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="hidden sm:inline">Export PDF</span>
            </button>
          </div>
          <CoachInsightsPanel
            studentId={selectedStudent.id}
            studentName={`${selectedStudent.first_name} ${selectedStudent.last_name}`}
          />
          <div className="mt-6">
            <ParcoursView studentId={selectedStudent.id} isCoach={true} />
          </div>
        </div>
      )}


      {/* Visits Tab */}
      {activeTab === "visits" && selectedStudent && studentDetail && (
        <div className="space-y-4 animate-fade-in">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => { setSelectedStudent(null); setActiveTab("overview"); }} className="btn-ghost p-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            </button>
            <div>
              <h3 className="font-semibold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>{selectedStudent.first_name} {selectedStudent.last_name}</h3>
              <p className="text-xs text-gray-500">Visites immobilières</p>
            </div>
          </div>
          {studentDetail.visits?.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center">
              <p className="text-gray-400">Aucune visite enregistrée</p>
            </div>
          ) : (
            <div className="space-y-3">
              {studentDetail.visits?.map(v => (
                <div key={v.id} className="glass-card rounded-2xl p-5">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-[#1a2744] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{v.address}</p>
                      <div className="flex gap-4 mt-1 text-sm">
                        {v.visit_date && <span className="text-gray-500">{v.visit_date}</span>}
                        {v.price && <span className="text-emerald-600 font-semibold">{Number(v.price).toLocaleString("fr-FR")} €</span>}
                      </div>
                      {v.comments && <p className="text-sm text-gray-500 mt-2">{v.comments}</p>}
                      {v.photos?.length > 0 && (
                        <div className="photo-grid mt-3">
                          {v.photos.map((p, i) => (
                            <div key={i} className="photo-item">
                              <img src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt="" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Notes Tab */}
      {activeTab === "notes" && selectedStudent && studentDetail && (
        <div className="space-y-4 animate-fade-in">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => { setSelectedStudent(null); setActiveTab("overview"); }} className="btn-ghost p-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            </button>
            <div>
              <h3 className="font-semibold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>{selectedStudent.first_name} {selectedStudent.last_name}</h3>
              <p className="text-xs text-gray-500">Notes & Cahier des charges</p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="glass-card rounded-2xl p-5">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Notes personnelles</h4>
              <p className="text-sm text-gray-500 whitespace-pre-wrap">{studentDetail.notes || "Aucune note"}</p>
            </div>
            <div className="glass-card rounded-2xl p-5">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Cahier des charges</h4>
              {studentDetail.cahier ? (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {Object.entries(studentDetail.cahier).filter(([k]) => k !== "student_id" && k !== "id").map(([k, v]) => (
                    <div key={k}>
                      <p className="text-xs text-gray-500 capitalize">{k.replace(/_/g, " ")}</p>
                      <p className="text-gray-900 font-medium">{String(v) || "-"}</p>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-gray-400">Non renseigné</p>}
            </div>
          </div>
        </div>
      )}

      {/* Odysboard Tab */}
      {activeTab === "odysboard" && selectedStudent && (
        <div className="animate-fade-in">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => { setSelectedStudent(null); setActiveTab("overview"); }} className="btn-ghost p-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            </button>
            <div>
              <h3 className="font-semibold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>{selectedStudent.first_name} {selectedStudent.last_name}</h3>
              <p className="text-xs text-gray-500">Données Odysboard</p>
            </div>
          </div>
          <OdysboardView studentId={selectedStudent.id} isCoachView={true} />
        </div>
      )}

      {/* Agenda Tab (per student) */}
      {activeTab === "agenda" && selectedStudent && (
        <div className="animate-fade-in" data-testid="coach-student-agenda">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => { setSelectedStudent(null); setActiveTab("overview"); }} className="btn-ghost p-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            </button>
            <div>
              <h3 className="font-semibold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>{selectedStudent.first_name} {selectedStudent.last_name}</h3>
              <p className="text-xs text-gray-500">Échéances et rendez-vous</p>
            </div>
          </div>
          <AgendaView studentId={selectedStudent.id} showSubscription={false} />
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === "settings" && (
        <div className="space-y-4 animate-fade-in">
          <NotificationPreferences />
          <div className="glass-card rounded-2xl p-6">
            <h3 className="font-semibold text-gray-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Lien Calendly</h3>
            <p className="text-sm text-gray-500 mb-4">Vos élèves pourront utiliser ce lien pour planifier des rendez-vous avec vous.</p>
            <div className="flex gap-3">
              <input
                data-testid="calendly-url-input"
                type="url"
                placeholder="https://calendly.com/votre-lien"
                className="input-dark flex-1"
                value={calendlyUrl}
                onChange={e => setCalendlyUrl(e.target.value)}
              />
              <button
                data-testid="save-calendly-btn"
                onClick={handleSaveCalendly}
                disabled={savingCalendly}
                className="btn-primary disabled:opacity-50"
              >
                {savingCalendly ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>

          <div className="glass-card rounded-2xl p-6">
            <h3 className="font-semibold text-gray-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Email Fathom</h3>
            <p className="text-sm text-gray-500 mb-4">
              L'adresse email avec laquelle vous enregistrez vos réunions Fathom (ex : ismael.riadhi@gmail.com).
              Vos enregistrements seront automatiquement rattachés à votre compte coach, même si cette adresse diffère de votre email de connexion.
            </p>
            <div className="flex gap-3">
              <input
                data-testid="fathom-email-input"
                type="email"
                placeholder="votre.email.fathom@gmail.com"
                className="input-dark flex-1"
                value={fathomEmail}
                onChange={e => setFathomEmail(e.target.value)}
              />
              <button
                data-testid="save-fathom-email-btn"
                onClick={handleSaveFathom}
                disabled={savingFathom}
                className="btn-primary disabled:opacity-50"
              >
                {savingFathom ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>

          <div className="glass-card rounded-2xl p-6">
            <h3 className="font-semibold text-gray-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Clé API Fathom (personnelle)</h3>
            <p className="text-sm text-gray-500 mb-3">
              Chaque coach configure sa <b>propre clé API</b> (générée sur son compte Fathom) pour importer son historique. Nécessaire uniquement pour le bouton <b>« Synchroniser Fathom »</b>.
              <br /><span className="text-xs">Générer la clé sur <a href="https://fathom.video/customize#api-access-header" target="_blank" rel="noopener noreferrer" className="underline text-blue-600">Fathom → Settings → API Access</a>.</span>
            </p>
            {hasFathomApiKey && (
              <div className="mb-3 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                Clé API personnelle configurée
              </div>
            )}
            <div className="flex gap-3">
              <input
                data-testid="fathom-api-key-input"
                type="password"
                placeholder={hasFathomApiKey ? "Remplacer la clé (laisser vide pour ne rien changer)" : "Coller votre clé API Fathom ici"}
                className="input-dark flex-1"
                value={fathomApiKey}
                onChange={e => setFathomApiKey(e.target.value)}
              />
              <button
                data-testid="save-fathom-api-key-btn"
                onClick={handleSaveFathomKey}
                disabled={savingFathomKey || !fathomApiKey}
                className="btn-primary disabled:opacity-50"
              >
                {savingFathomKey ? "..." : "Enregistrer"}
              </button>
            </div>
            {hasFathomApiKey && (
              <button
                onClick={async () => {
                  if (!window.confirm("Supprimer votre clé API Fathom ?")) return;
                  setSavingFathomKey(true);
                  try {
                    await api.put("/coach/settings", { fathom_api_key: "" });
                    toast({ title: "Clé API supprimée" });
                    const me = await api.get("/auth/me");
                    if (me.data) { localStorage.setItem("user", JSON.stringify(me.data)); window.location.reload(); }
                  } catch (e) {
                    toast({ variant: "destructive", title: "Erreur" });
                  } finally { setSavingFathomKey(false); }
                }}
                className="text-xs text-red-600 hover:underline mt-2"
                data-testid="delete-fathom-api-key-btn"
              >
                Supprimer ma clé
              </button>
            )}
          </div>

          <div className="glass-card rounded-2xl p-6 border-2 border-blue-100 bg-blue-50/30">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              Webhook Fathom (import automatique)
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Pour que <b>chaque nouvel enregistrement</b> soit importé automatiquement (sans cliquer sur Sync), configurez ce webhook <b>dans votre propre compte Fathom</b>. À faire une seule fois.
            </p>
            <ol className="text-sm text-gray-700 space-y-1 mb-4 list-decimal list-inside">
              <li>Connectez-vous à Fathom (votre compte)</li>
              <li>Allez sur <a href="https://fathom.video/customize#api-access-header" target="_blank" rel="noopener noreferrer" className="underline text-blue-600">Settings → API Access</a></li>
              <li>Générez une clé API si ce n&apos;est pas déjà fait</li>
              <li>Cliquez <b>Manage → Add Webhook</b></li>
              <li>Collez l&apos;URL ci-dessous, sélectionnez <b>My own meetings</b>, cochez Transcript + Summary + Action items</li>
            </ol>
            <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-2">
              <code className="text-xs sm:text-sm text-gray-800 break-all flex-1" data-testid="fathom-webhook-url">
                https://odys-mentoring.odysinvest.com/api/webhooks/fathom
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText("https://odys-mentoring.odysinvest.com/api/webhooks/fathom");
                  toast({ title: "URL copiée !" });
                }}
                className="btn-ghost text-xs px-3 py-1.5 flex-shrink-0"
                data-testid="copy-webhook-url-btn"
              >
                Copier
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              💡 Vérifiez aussi que votre <b>Email Fathom</b> ci-dessus correspond bien à l&apos;email du compte utilisé pour enregistrer.
            </p>
          </div>
        </div>
      )}

      {/* Step Modal */}
      {showStepModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md animate-fade-in-up shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-5" style={{ fontFamily: "Outfit, sans-serif" }}>
              {editingStep ? "Modifier l'étape" : "Nouvelle étape"}
            </h3>
            <div className="space-y-3">
              <input data-testid="step-title-input" type="text" placeholder="Titre de l'étape*" className="input-dark w-full" value={stepForm.title} onChange={e => setStepForm({ ...stepForm, title: e.target.value })} />
              <textarea rows={3} placeholder="Description (optionnelle)" className="input-dark w-full resize-none" value={stepForm.description} onChange={e => setStepForm({ ...stepForm, description: e.target.value })} />
              <input type="date" className="input-dark w-full" value={stepForm.deadline} onChange={e => setStepForm({ ...stepForm, deadline: e.target.value })} />
              <select className="input-dark w-full" value={stepForm.status} onChange={e => setStepForm({ ...stepForm, status: e.target.value })}>
                <option value="pending">En attente</option>
                <option value="in_progress">En cours</option>
                <option value="completed">Terminé</option>
              </select>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => { setShowStepModal(false); setEditingStep(null); }} className="btn-ghost flex-1">Annuler</button>
              <button data-testid="save-step-btn" onClick={handleSaveStep} disabled={!stepForm.title.trim()} className="btn-primary flex-1 disabled:opacity-50">
                {editingStep ? "Modifier" : "Créer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
